import type { NextPage, NextPageContext } from 'next';

import { CustomLoader } from '@/components/elements/loader';
import { FavoriteBlock, FavoriteBlockEmpty } from '@/components/modules/favorite';
import { StyledFavoriteLoader } from '@/components/modules/favorite/styled';
import { wrapperReduxToolkit } from '@/store';
import { useAppSelector } from '@/store/hooks';
import { loadWithPopularProducts } from '@/store/utils/load-with-popular-products';

import { Footer } from '@/widgets/footer';
import { Header } from '@/widgets/header';

import { HeadMeta, getMetaDataPage } from '@/entities/meta-seo';
import { TRentProduct, PopularProducts } from '@/entities/product';

import { useTranslate } from '@/shared/hooks/use-translate';
import { Heading } from '@/shared/ui/heading';
import { Layout } from '@/shared/ui/layout';
import { CatchingErrorLayout } from '@/shared/ui/layout/catching-error';
import { PageSection } from '@/shared/ui/page-section';

type TProps = {
    title: string;
    description: string;
    statusCode: number;
    popularProducts: TRentProduct[];
};

const Favorite: NextPage<TProps> = ({ description, statusCode, title, popularProducts }) => {
    const { itemsFavorite, isLoaded } = useAppSelector((state) => state.favorite);

    const { t } = useTranslate();

    const content =
        itemsFavorite?.length > 0 ? (
            <FavoriteBlock favorites={itemsFavorite} />
        ) : (
            <>
                <FavoriteBlockEmpty />
                <PageSection title={t('Самые популярные 🔥')}>
                    <PopularProducts products={popularProducts} />
                </PageSection>
            </>
        );

    return (
        <CatchingErrorLayout statusCode={statusCode}>
            <Layout header={<Header />} footer={<Footer />}>
                <HeadMeta title={title} description={description} />
                <Layout.Content>
                    <Heading as="h1">{t('Избранное')}</Heading>
                    {isLoaded ? (
                        content
                    ) : (
                        <StyledFavoriteLoader>
                            <CustomLoader />
                        </StyledFavoriteLoader>
                    )}
                </Layout.Content>
            </Layout>
        </CatchingErrorLayout>
    );
};

export default Favorite;

Favorite.getInitialProps = wrapperReduxToolkit.getInitialPageProps(
    ({ dispatch, getState }) =>
        async (context: NextPageContext) => {
            const { locale } = context;

            const { popularProducts } = await loadWithPopularProducts({
                getState,
                dispatch,
                locale: locale as string,
                context,
            });

            const metaData = await getMetaDataPage('/favorite', locale as string);

            return { ...metaData, popularProducts };
        },
);
